import React from 'react';

import { Badge, Button, Label, Card, Notification } from '@ergeon/core-components';
import { UserPlusIcon, UserCircleIcon } from '@ergeon/icons/hero/micro';

import { Participant } from '../../types';
import Table, { TableColumn } from '../Table';

type Props = {
  participants: Participant[];
  onInviteAgent?: () => void;
  disableInvite?: boolean;
  error?: string | null;
};

const ParticipantsTable: React.FC<Props> = ({ participants, onInviteAgent, disableInvite, error }) => {
  const getStatusBadge = (status: Participant['status']) => {
    const statusConfig = {
      inviting: { flavor: 'primary', text: 'Inviting' },
      joined: { flavor: 'success', text: 'Joined' },
      left: { flavor: 'warning', text: 'Left' },
      failed: { flavor: 'danger', text: 'Failed' },
      unknown: { flavor: 'regular', text: 'Unknown' },
    } as const;

    const config = statusConfig[status] ?? statusConfig.unknown;
    return <Badge flavor={config.flavor} value={config.text} />;
  };

  const formatClient = (client: string) => {
    // Remove 'client:' prefix if it exists
    return client.replace(/^client:/, '');
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateTimeString;
    }
  };

  const columns: TableColumn<Participant>[] = [
    {
      header: 'Agent',
      accessor: (participant) => (
        <div className="flex items-center gap-1 nowrap">
          <UserCircleIcon className="inline-block size-5 text-gray-500 dark:text-gray-400" />
          {formatClient(participant.client)}
        </div>
      ),
      className: 'font-bold text-gray-900 dark:text-gray-100 no-wrap paragraph-2',
    },
    {
      header: 'Status',
      accessor: (participant) => getStatusBadge(participant.status),
    },
    {
      header: 'Joined At',
      accessor: (participant) => formatDateTime(participant.created_at),
      className: 'text-gray-700 dark:text-gray-400 paragraph-2',
    },
  ];

  const inviteButton = onInviteAgent && (
    <Button
      aria-label="Invite another agent to the call"
      disabled={disableInvite}
      flavor="regular"
      onClick={onInviteAgent}
      size="small"
      taste="line"
      title={disableInvite ? 'No active call to invite agent to' : 'Invite another Agent to the Call'}>
      <UserPlusIcon className="size-4 shrink-0" />
      Invite Agent
    </Button>
  );

  const renderHeader = (title: string) => (
    <div className="flex justify-between items-start">
      <Label>{title}</Label>
      {inviteButton}
    </div>
  );

  if (participants.length === 0) {
    return (
      <Card className="relative flex flex-col items-stretch gap-2 p-4 w-full" flavor="base" zIndex="1">
        {renderHeader('Participants')}
        {error ? (
          <Notification mode="embed" type="Error">
            {error}
          </Notification>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 paragraph-3">No participants in this call.</p>
        )}
      </Card>
    );
  }

  return (
    <Card className="relative flex flex-col items-stretch gap-2 p-4 w-full" flavor="base" zIndex="1">
      {renderHeader(`Participants (${participants.length})`)}
      {error ? (
        <Notification mode="embed" type="Error">
          {error}
        </Notification>
      ) : (
        <Table columns={columns} data={participants} getRowKey={(participant) => participant.call_sid} />
      )}
    </Card>
  );
};

export default ParticipantsTable;
